import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTournament, deletePlayer, addPlayer, getEventConfirmedMembers, startTournament, updatePlayerOrder } from '../api/tournamentsApi';
import { getEvent } from '../api/eventsApi';
import { Button, NumberInput, Field } from '../components/UI';
import { useToast } from '../components/Toast';
import SectionCard from '../components/tournament/SectionCard';
import MatchScoringField from '../components/tournament/MatchScoringField';
import TournamentMemberList from '../components/tournament/TournamentMemberList';
import { IconArrowLeft, IconTrophy } from '../components/tournament/TournamentIcons';
import { SPORT_LABELS, RANKING_OPTIONS, RANKING_HINTS, formatSupportsSeeding } from '../components/tournament/tournamentConstants';

// ─── Константы ────────────────────────────────────────────────────────────────

const FORMAT_OPTIONS = [
  {
    value: 'AMERICANO',
    label: 'Americano',
    description: 'Партнёры меняются каждый тур. Каждый играет с каждым.',
    available: true,
  },
  {
    value: 'MEXICANO',
    label: 'Mexicano',
    description: 'Пары формируются по текущему рейтингу. Туры открываются по ходу турнира.',
    available: true,
  },
  {
    value: 'BEAT_THE_BOX',
    label: 'Beat the Box',
    description: 'Два этапа: боксы по 4, после этапа 1 — пересортировка по результатам. Нужно ровно 8/12/16 игроков.',
    available: true,
  },
  {
    value: 'KING_OF_COURT',
    label: 'King of the Court',
    description: 'Победитель остаётся на корте.',
    available: false,
  },
  {
    value: 'ROUND_ROBIN',
    label: 'Round Robin',
    description: 'Фиксированные пары, каждая играет с каждой.',
    available: false,
  },
];

// Минимум активных игроков для немедленного старта (doubles: teamSize 2 × 2 команды).
// Americano и Mexicano — оба парные форматы, поэтому минимум одинаков.
const MIN_PLAYERS_TO_START = 4;

// ─── Beat the Box: требования к составу ───────────────────────────────────────
// Строго кратно 4, минимум 8. Бэкенд — источник истины, фронт лишь подсвечивает.
const BTB_MIN_PLAYERS    = 8;
const BTB_PLAYERS_PER_BOX = 4;

const isBtbCountValid   = (n) => n >= BTB_MIN_PLAYERS && n % BTB_PLAYERS_PER_BOX === 0;
// Рекомендуемое число кортов для BtB — строго players/4
const btbSuggestedCourts = (n) => (n >= BTB_MIN_PLAYERS && n % BTB_PLAYERS_PER_BOX === 0)
  ? n / BTB_PLAYERS_PER_BOX
  : null;

// ─── Page ─────────────────────────────────────────────────────────────────────

const CreateTournamentPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Читаем параметры из URL
  const params = new URLSearchParams(window.location.search);
  const eventId    = params.get('eventId');
  const sportFromUrl = params.get('sport') || '';
  const formatFromUrl = params.get('format') || '';  // если есть — показываем форму, иначе — выбор формата

  // ── Состояние формы ────────────────────────────────────────────────────────
  // format выводим прямо из URL (не из useState): при выборе формата на экране
  // выбора компонент не перемонтируется, поэтому useState-инициализатор «застрял»
  // бы на первом значении. Производное значение всегда соответствует URL.
  const format = formatFromUrl || 'AMERICANO';
  const [courtsCount, setCourtsCount] = useState('');
  // Дефолт режима счёта зависит от формата: Mexicano — «на время», остальные —
  // «до набора очков». Пока пользователь не переключил тумблер вручную
  // (matchScoringOverride === null), значение вычисляется из формата.
  const [matchScoringOverride, setMatchScoringOverride] = useState(null);
  const matchScoring = matchScoringOverride ?? (format === 'MEXICANO' ? 'TIME' : 'POINTS');
  const [matchDurationMinutes, setMatchDurationMinutes] = useState(15);
  // Дефолт режима таблицы лидеров зависит от формата: Beat the Box — «по победам»,
  // остальные — «сумма очков». Пока пользователь не переключил вручную
  // (rankingModeOverride === null), значение вычисляется из формата.
  const [rankingModeOverride, setRankingModeOverride] = useState(null);
  const rankingMode = rankingModeOverride ?? (format === 'BEAT_THE_BOX' ? 'FOOTBALL' : 'POINTS');

  const [starting, setStarting] = useState(false);     // «Создать и запустить»
  const [savingDraft, setSavingDraft] = useState(false); // «Сохранить черновик»
  const [errors, setErrors] = useState({});

  // Bottom sheet для быстрого ввода количества кортов
  const [courtsSheet, setCourtsSheet]     = useState(false); // открыт ли шит
  const [pendingStart, setPendingStart]   = useState(null);  // true/false — что запустить после ввода
  const [courtsSheetValue, setCourtsSheetValue] = useState('');

  // ── Участники ──────────────────────────────────────────────────────────────
  // Set с organizationMemberId тех, кого ИСКЛЮЧАЕМ из турнира
  const [members, setMembers]         = useState([]);       // CONFIRMED + WAITING участники события
  const [membersLoading, setMembersLoading] = useState(false);
  const [excluded, setExcluded]       = useState(new Set()); // снятые галочки
  // organizationId события — нужен AddParticipantPanel внутри TournamentMemberList
  // (поиск участников организации при добавлении нового человека).
  const [organizationId, setOrganizationId] = useState(null);
  // Актуальный members «мимо» замыкания loadMembers — чтобы при рефетче после
  // добавления участника не сбрасывать уже выставленные пользователем галочки.
  const membersRef = useRef([]);
  useEffect(() => { membersRef.current = members; }, [members]);

  const selectedCount = members.length - excluded.size;

  // ── Посев (random / manual order) — только Mexicano / Beat the Box ──────────
  const seedingSupported = formatSupportsSeeding(format);
  const [randomSeeding, setRandomSeeding] = useState(true); // дефолт — случайный
  const [manualOrder, setManualOrder]     = useState([]);   // organizationMemberId в ручном порядке

  // Включённые участники (галочка установлена), упорядоченные по manualOrder;
  // новые (которых нет в manualOrder) добавляются в конец в исходном порядке.
  const includedMembers = members.filter(m => !excluded.has(m.organizationMemberId));
  const orderedIncluded = [
    ...manualOrder
      .map(id => includedMembers.find(m => m.organizationMemberId === id))
      .filter(Boolean),
    ...includedMembers.filter(m => !manualOrder.includes(m.organizationMemberId)),
  ];

  // ── Beat the Box: автоподсказка кортов ────────────────────────────────────
  // Если выбран формат BtB и состав валиден — предлагаем нужное число кортов.
  // Пользователь может переопределить вручную; бэкенд всё равно проверит.
  const isBtb = format === 'BEAT_THE_BOX';
  const btbCourtsHint = isBtb ? btbSuggestedCourts(selectedCount) : null;
  const btbPlayersValid = !isBtb || isBtbCountValid(selectedCount);

  // Загрузка/перезагрузка состава. При первом вызове membersRef.current пуст — все
  // участники трактуются как «новые», что воспроизводит исходное поведение (лист
  // ожидания выключен по умолчанию). При повторном вызове (после добавления через
  // AddParticipantPanel) уже выставленные галочки не трогаем, а новый человек
  // получает тот же дефолт: выключен, если в листе ожидания, иначе включён.
  const loadMembers = useCallback(() => {
    if (!eventId) return Promise.resolve();
    setMembersLoading(true);
    return getEventConfirmedMembers(eventId)
      .then(data => {
        const all = data || [];
        const prevIds = new Set(membersRef.current.map(m => m.organizationMemberId));
        setMembers(all);
        setExcluded(prevExcluded => {
          const currentIds = new Set(all.map(m => m.organizationMemberId));
          const next = new Set(Array.from(prevExcluded).filter(id => currentIds.has(id)));
          all.forEach(m => {
            if (!prevIds.has(m.organizationMemberId) && m.waitingList) {
              next.add(m.organizationMemberId);
            }
          });
          return next;
        });
      })
      .catch(() => setMembers([]))
      .finally(() => setMembersLoading(false));
  }, [eventId]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // organizationId — отдельным лёгким запросом события (без бэкенд-правок:
  // просто переиспользуем существующий getEvent, который уже отдаёт это поле).
  useEffect(() => {
    if (!eventId) return;
    getEvent(eventId)
      .then(ev => setOrganizationId(ev?.organizationId ?? null))
      .catch(() => {});
  }, [eventId]);

  const toggleMember = (id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Для BtB автоматически предзаполняем поле кортов при изменении состава участников,
  // если пользователь ещё не вводил значение вручную.
  useEffect(() => {
    if (!isBtb) return;
    const suggested = btbSuggestedCourts(selectedCount);
    if (suggested !== null && !courtsCount) {
      setCourtsCount(String(suggested));
    }
  }, [isBtb, selectedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // start=true  → создать турнир и сразу запустить (минуя экран DRAFT)
  // start=false → создать черновик, открыть страницу турнира в статусе DRAFT
  // courtsOverride — значение кортов из bottom sheet (минует стейт)
  const submit = async (start, courtsOverride) => {
    const effectiveCourts = courtsOverride !== undefined ? courtsOverride : courtsCount;
    const errs = {};
    if (!sportFromUrl) errs.sport = 'Вид спорта не задан — вернитесь к событию и попробуйте снова';
    if (!effectiveCourts || effectiveCourts < 1) errs.courtsCount = 'Минимум 1 корт';
    if (matchScoring === 'TIME' && (!matchDurationMinutes || matchDurationMinutes < 1)) {
      errs.matchDurationMinutes = 'Укажите длительность матча';
    }

    if (Object.keys(errs).length) {
      // Если единственная проблема — не заполнены корты, открываем bottom sheet
      const onlyCourts = Object.keys(errs).length === 1 && errs.courtsCount;
      if (onlyCourts) {
        setCourtsSheetValue(courtsCount || '');
        setPendingStart(start);
        setCourtsSheet(true);
        return;
      }
      setErrors(errs);
      return;
    }
    setErrors({});
    start ? setStarting(true) : setSavingDraft(true);

    const body = {
      format,
      sport: sportFromUrl || undefined,
      courtsCount: Number(effectiveCourts),
      matchScoring,
      rankingMode,
      ...(matchScoring === 'TIME' ? { matchDurationMinutes: Number(matchDurationMinutes) } : {}),
      ...(seedingSupported ? { randomSeeding } : {}),
    };

    let tournament;
    try {
      // Создаём турнир — бэк автоматически добавляет всех CONFIRMED участников
      tournament = await createTournament(eventId, body);

      // Удаляем тех, кого сняли галочкой (среди CONFIRMED, добавленных бэком автоматически)
      if (excluded.size > 0) {
        const playersToRemove = (tournament.players || []).filter(
          p => excluded.has(p.organizationMemberId)
        );
        await Promise.all(
          playersToRemove.map(p => deletePlayer(tournament.publicId, p.id))
        );
      }

      // Добавляем WAITING-участников, у которых тумблер включён
      const waitingToAdd = members.filter(
        m => m.waitingList && !excluded.has(m.organizationMemberId)
      );
      await Promise.all(
        waitingToAdd.map(m => addPlayer(tournament.publicId, { organizationMemberId: m.organizationMemberId }))
      );

      // Ручной посев: проставляем порядок до старта (чтобы первая генерация учла его).
      // Эндпоинт принимает organizationMemberId напрямую — маппинг на playerId не нужен.
      // Не критично: при сбое порядок просто не применится, турнир останется валидным.
      if (seedingSupported && !randomSeeding) {
        const orderedIds = orderedIncluded.map(m => m.organizationMemberId);
        if (orderedIds.length) {
          try {
            await updatePlayerOrder(tournament.publicId, orderedIds);
          } catch (_) {
            // посев необязателен — игнорируем сбой
          }
        }
      }

      if (start) {
        await startTournament(tournament.publicId);
      }

      navigate(`/tournaments/${tournament.publicId}`, { replace: true });
    } catch (err) {
      // Турнир мог уже создаться, а упасть только старт — не теряем черновик,
      // отправляем пользователя на страницу турнира (DRAFT), где он дозапустит вручную.
      if (tournament?.publicId) {
        addToast(`Не удалось запустить: ${err.message}. Турнир сохранён как черновик.`, 'error');
        navigate(`/tournaments/${tournament.publicId}`, { replace: true });
      } else {
        addToast(`Ошибка: ${err.message}`, 'error');
      }
    } finally {
      setStarting(false);
      setSavingDraft(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!eventId) {
    return (
      <div style={{ padding: 24, color: '#b91c1c', fontSize: 14 }}>
        Не передан eventId. Вернитесь к событию и попробуйте снова.
      </div>
    );
  }

  // ── Экран 1: выбор формата (нет format в URL) ──────────────────────────────
  if (!formatFromUrl) {
    const goWithFormat = (f) => {
      const next = new URLSearchParams(params);
      next.set('format', f);
      navigate(`/tournaments/new?${next.toString()}`, { replace: false });
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{
          background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
          padding: '14px 16px 16px', color: '#fff', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8, padding: '5px 10px', color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              <IconArrowLeft />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconTrophy />
              </div>
              <div>
                <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Выбор формата</h1>
                {sportFromUrl && (
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                    {SPORT_LABELS[sportFromUrl] || sportFromUrl}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {FORMAT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => opt.available && goWithFormat(opt.value)}
              disabled={!opt.available}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '14px 16px', borderRadius: 14,
                border: '1.5px solid #e5e7eb',
                background: opt.available ? '#fff' : '#fafafa',
                cursor: opt.available ? 'pointer' : 'not-allowed',
                opacity: opt.available ? 1 : 0.55,
                textAlign: 'left',
                boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                    {opt.label}
                  </span>
                  {!opt.available && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      background: '#f3f4f6', color: '#9ca3af',
                      borderRadius: 99, padding: '1px 7px',
                      border: '1px solid #e5e7eb',
                    }}>
                      Скоро
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3, lineHeight: 1.4 }}>
                  {opt.description}
                </div>
              </div>
              {opt.available && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 3, flexShrink: 0 }}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
        padding: '14px 16px 16px',
        color: '#fff',
        flexShrink: 0,
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '5px 10px',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          >
            <IconArrowLeft />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconTrophy />
            </div>
            <div>
              <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Новый турнир</h1>
              {sportFromUrl && (
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                  {SPORT_LABELS[sportFromUrl] || sportFromUrl}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Form ── */}
      <div style={{ padding: '16px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Корты */}
        <SectionCard title="Площадки">
          <Field label="Количество кортов" error={errors.courtsCount}>
            <NumberInput
              min={1}
              max={20}
              value={courtsCount}
              onChange={e => {
                setCourtsCount(e.target.value);
                if (errors.courtsCount) setErrors(prev => ({ ...prev, courtsCount: '' }));
              }}
              error={errors.courtsCount}
            />
          </Field>
          {/* Beat the Box: подсказка про соответствие кортов и состава */}
          {isBtb && btbCourtsHint !== null && (
            <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>
              Для {selectedCount} игроков нужно {btbCourtsHint} {btbCourtsHint === 1 ? 'корт' : btbCourtsHint < 5 ? 'корта' : 'кортов'} (по 4 игрока на корт).
            </div>
          )}
          {isBtb && !btbPlayersValid && selectedCount > 0 && (
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
              Beat the Box: нужно ровно 8, 12 или 16 игроков (сейчас {selectedCount}).
            </div>
          )}
          {/* Для не-BtB форматов: быстрые варианты кортов 1..floor(selectedCount/4) */}
          {!isBtb && selectedCount >= 4 && (() => {
            const maxCourts = Math.floor(selectedCount / 4);
            if (maxCourts < 1) return null;
            const options = Array.from({ length: maxCourts }, (_, i) => i + 1);
            return (
              <div style={{ marginTop: 6 }}>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {options.map(n => (
                    <button
                      key={n}
                      onClick={() => {
                        setCourtsCount(String(n));
                        if (errors.courtsCount) setErrors(prev => ({ ...prev, courtsCount: '' }));
                      }}
                      style={{
                        padding: '3px 12px',
                        borderRadius: 20,
                        border: '1.5px solid',
                        borderColor: String(courtsCount) === String(n) ? '#d97706' : '#e5e7eb',
                        background: String(courtsCount) === String(n)
                          ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                          : '#f9fafb',
                        color: String(courtsCount) === String(n) ? '#fff' : '#374151',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </SectionCard>

        {/* Режим счёта */}
        <SectionCard title="Режим матча">
          <MatchScoringField
            matchScoring={matchScoring}
            onMatchScoringChange={(val) => {
              setMatchScoringOverride(val);
              setErrors(prev => ({ ...prev, matchDurationMinutes: '' }));
            }}
            matchDurationMinutes={matchDurationMinutes}
            onMatchDurationChange={setMatchDurationMinutes}
            durationError={errors.matchDurationMinutes}
            onDurationErrorClear={() => setErrors(prev => ({ ...prev, matchDurationMinutes: '' }))}
          />
        </SectionCard>

        {/* Таблица лидеров */}
        <SectionCard title="Таблица лидеров">
          <div style={{ display: 'flex', borderRadius: 10, border: '1.5px solid #e5e7eb', overflow: 'hidden' }}>
            {RANKING_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setRankingModeOverride(opt.value)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  background: rankingMode === opt.value
                    ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                    : '#f9fafb',
                  color: rankingMode === opt.value ? '#fff' : '#374151',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
            {RANKING_HINTS[rankingMode]}
          </div>
        </SectionCard>

        {/* Участники — единый список с инлайн-сортировкой в ручном режиме */}
        <TournamentMemberList
          members={members}
          loading={membersLoading}
          isActive={(id) => !excluded.has(id)}
          onToggle={toggleMember}
          activeCount={selectedCount}
          seedingSupported={seedingSupported}
          randomOrder={randomSeeding}
          onRandomOrderChange={setRandomSeeding}
          orderItems={orderedIncluded}
          onReorder={(items) => setManualOrder(items.map(m => m.organizationMemberId))}
          eventId={eventId}
          organizationId={organizationId}
          onParticipantAdded={loadMembers}
        />

      </div>

      {/* ── Bottom sheet: быстрый ввод кортов ── */}
      {courtsSheet && (
        <>
          {/* Затемнение */}
          <div
            onClick={() => setCourtsSheet(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 110,
              background: 'rgba(0,0,0,0.35)',
            }}
          />
          {/* Шит */}
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 120,
            maxWidth: 480, margin: '0 auto',
            background: '#fff',
            borderRadius: '18px 18px 0 0',
            padding: '20px 20px 32px',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          }}>
            {/* Ручка */}
            <div style={{
              width: 36, height: 4, borderRadius: 2, background: '#e5e7eb',
              margin: '0 auto 18px',
            }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              Сколько кортов?
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Укажите количество кортов, на которых пройдут матчи
            </div>
            <NumberInput
              min={1}
              max={20}
              value={courtsSheetValue}
              onChange={e => setCourtsSheetValue(e.target.value)}
              autoFocus
            />
            {/* Быстрый выбор кортов в bottom sheet (только для не-BtB) */}
            {!isBtb && selectedCount >= 4 && (() => {
              const maxCourts = Math.floor(selectedCount / 4);
              if (maxCourts < 1) return null;
              const options = Array.from({ length: maxCourts }, (_, i) => i + 1);
              return (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {options.map(n => (
                      <button
                        key={n}
                        onClick={() => setCourtsSheetValue(String(n))}
                        style={{
                          padding: '4px 14px',
                          borderRadius: 20,
                          border: '1.5px solid',
                          borderColor: String(courtsSheetValue) === String(n) ? '#d97706' : '#e5e7eb',
                          background: String(courtsSheetValue) === String(n)
                            ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                            : '#f9fafb',
                          color: String(courtsSheetValue) === String(n) ? '#fff' : '#374151',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.12s',
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            <button
              onClick={() => {
                const val = Number(courtsSheetValue);
                if (!val || val < 1) return;
                setCourtsCount(courtsSheetValue); // синхронизируем стейт формы
                setCourtsSheet(false);
                submit(pendingStart, val); // передаём значение напрямую, не дожидаясь setState
              }}
              disabled={!courtsSheetValue || Number(courtsSheetValue) < 1}
              style={{
                marginTop: 14, width: '100%',
                padding: '13px', borderRadius: 12, border: 'none',
                background: (!courtsSheetValue || Number(courtsSheetValue) < 1)
                  ? '#e5e7eb'
                  : 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                color: (!courtsSheetValue || Number(courtsSheetValue) < 1) ? '#9ca3af' : '#fff',
                fontSize: 15, fontWeight: 700,
                cursor: (!courtsSheetValue || Number(courtsSheetValue) < 1) ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              Готово
            </button>
          </div>
        </>
      )}

      {/* ── Кнопки ──
          Не фиксируем к низу экрана: на мобильном при открытой клавиатуре
          (ввод кортов, редактирование списка участников) фиксированная панель
          съедала бы и без того малое полезное пространство. Кнопки — обычный
          блок в конце контента, пользователь докручивает до них сам. */}
      <div style={{
        padding: '12px 16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: '#f8fafc',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {/* Подсказка, если игроков не хватает для немедленного старта */}
        {!membersLoading && selectedCount < MIN_PLAYERS_TO_START && (
          <div style={{
            fontSize: 12, color: '#b45309', textAlign: 'center',
            background: '#fff9f5', border: '1px solid #fde8d8',
            borderRadius: 10, padding: '7px 10px',
          }}>
            Для запуска нужно минимум {MIN_PLAYERS_TO_START} игрока (сейчас {selectedCount}).
          </div>
        )}
        {/* Beat the Box: предупреждение о некорректном составе */}
        {!membersLoading && isBtb && selectedCount >= MIN_PLAYERS_TO_START && !btbPlayersValid && (
          <div style={{
            fontSize: 12, color: '#b45309', textAlign: 'center',
            background: '#fff9f5', border: '1px solid #fde8d8',
            borderRadius: 10, padding: '7px 10px',
          }}>
            Beat the Box: нужно ровно 8, 12 или 16 игроков. Сейчас {selectedCount} — добавьте или снимите галочку.
          </div>
        )}

        <Button
          variant="ghost"
          fullWidth
          loading={savingDraft}
          disabled={starting}
          onClick={() => submit(false)}
          style={{ border: '1.5px solid #d1d5db' }}
        >
          Сохранить черновик
        </Button>

        <Button
          variant="primary"
          fullWidth
          loading={starting}
          disabled={savingDraft || selectedCount < MIN_PLAYERS_TO_START || !btbPlayersValid}
          onClick={() => submit(true)}
          style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }}
        >
          {selectedCount > 0
            ? `Создать и запустить · ${selectedCount} игроков`
            : 'Создать и запустить'}
        </Button>
      </div>
    </div>
  );
};

export default CreateTournamentPage;
